import { getApiBaseUrl } from "../config/runtimeConfig";
import { ApiError } from "../obras/obrasApi";

const apiBaseUrl = getApiBaseUrl();

export { ApiError };

export interface WorkPhotoAuthorDto {
  id: string | null;
  name: string;
}

export interface WorkPhotoDto {
  id: number;
  workId: number;
  fileName: string;
  contentType: string;
  sizeInBytes: number;
  note: string | null;
  author: WorkPhotoAuthorDto;
  isImage: boolean;
  isVideo: boolean;
  created: string;
  lastModified: string;
}

export interface WorkPhotosVm {
  photos: WorkPhotoDto[];
}

export interface WorkPhotoFilters {
  from?: string;
  to?: string;
  author?: string;
}

async function request<T>(path: string, options?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBaseUrl}${path}`, {
    ...options,
    credentials: "include",
  });

  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }

  const text = await response.text();
  if (!text) {
    return undefined as T;
  }

  return JSON.parse(text) as T;
}

export function getWorkPhotos(
  workId: number,
  filters?: WorkPhotoFilters,
): Promise<WorkPhotosVm> {
  const params = new URLSearchParams();
  params.set("workId", String(workId));
  if (filters?.from) params.set("from", filters.from);
  if (filters?.to) params.set("to", filters.to);
  if (filters?.author) params.set("author", filters.author);
  return request<WorkPhotosVm>(`/api/workphotos?${params.toString()}`);
}

export function uploadWorkPhoto(
  workId: number,
  file: File,
  note?: string,
): Promise<WorkPhotoDto> {
  const formData = new FormData();
  formData.append("file", file, file.name || "ficheiro");
  formData.append("workId", String(workId));
  if (note && note.trim()) {
    formData.append("note", note.trim());
  }

  // No Content-Type header: the browser sets the multipart boundary itself.
  return request<WorkPhotoDto>("/api/workphotos", {
    method: "POST",
    body: formData,
  });
}

export function updateWorkPhotoNote(
  id: number,
  note: string | null,
): Promise<WorkPhotoDto> {
  return request<WorkPhotoDto>(`/api/workphotos/${id}/note`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ note }),
  });
}

export function deleteWorkPhoto(id: number): Promise<void> {
  return request<void>(`/api/workphotos/${id}`, {
    method: "DELETE",
  });
}

// Direct URL to the file stream. Used as the <video src> (the backend serves the
// media inline with range support). For images we fetch the blob instead — see below.
export const getWorkPhotoFileUrl = (id: number): string => {
  return `${apiBaseUrl}/api/workphotos/${id}/file`;
};

// The media blob is served by an authenticated endpoint over a PRIVATE blob, so it
// cannot be used directly as an <img src> (in production the API is cross-origin).
// Fetch it with credentials and let the caller build an objectURL for previewing.
export async function fetchWorkPhotoBlob(id: number): Promise<Blob> {
  const response = await fetch(getWorkPhotoFileUrl(id), {
    credentials: "include",
  });
  if (!response.ok) {
    const text = await response.text();
    throw new ApiError(response.status, text || response.statusText);
  }
  return response.blob();
}
